// Shared visual language for the School Admin Portal — ported directly from the
// Learning Hub in the consumer app (web-frontend: components/ai-learn/shared.js
// + HomeView.jsx) so this feels like the same product instead of a generic
// internal MUI admin panel. Same palette, not a new one — the difference is
// shadow layering, radius, and hover motion.
export const bg = "#FAF8F5";
export const bgGradient = "#FAF8F5";
export const surface = "#FFFFFF";
export const surfaceRaised = "#F5F2EF";
export const surfaceHover = "rgba(192,30,46,0.06)";
export const border = "#E8E4DF";
export const borderStrong = "rgba(26,26,26,0.16)";
export const textPrimary = "#1a1a1a";
export const textSecondary = "#8a8580";
export const textMuted = "#b5b0aa";
export const accent = "#c01e2e";
export const accentHover = "#a81828";
export const accentSoft = "rgba(192,30,46,0.07)";
export const accentBorder = "rgba(192,30,46,0.18)";
export const gold = "#ffc107";
export const navy = "#1e3a5f";
export const font = "'Rubik', system-ui, sans-serif";

export const success = "#16a34a";
export const error = "#dc2626";
export const warning = "#d97706";
export const infoBlue = "#2563eb";

// Learn Mode's exact lesson-rating scheme (web-frontend components/ai-learn/shared.js
// getRating) — a topic rated "Good" here means the same 70-89% band it means in the
// student's own Learn Mode view.
export const LEARN_RATING_COLORS = {
  excellent: success,
  good: infoBlue,
  needs_work: warning,
  retry: accent,
};
export const LEARN_RATING_LABELS = {
  excellent: "Excellent",
  good: "Good",
  needs_work: "Needs Work",
  retry: "Retry",
};

// Learning Hub's exact shadow recipe: a near-invisible resting shadow, a
// slightly heavier one for elevated cards, and a lift shadow for hover.
export const shadow = "0 1px 3px rgba(0,0,0,0.04)";
export const shadowMed = "0 4px 16px rgba(0,0,0,0.07)";
export const shadowLifted = "0 8px 28px rgba(0,0,0,0.12)";
export const easing = "cubic-bezier(0.4, 0, 0.2, 1)";

export const glassCard = {
  backgroundColor: surface,
  border: `1px solid ${border}`,
  borderRadius: "14px",
  boxShadow: shadow,
};

// Dark accent-card treatment — reserved for hero/spotlight moments (e.g. the
// Book a Meeting header), not the whole page.
export const heroGradient = "linear-gradient(135deg, #1a1a1a 0%, #232326 55%, #1e3a5f 130%)";

export const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: surface,
    borderRadius: "10px",
    color: textPrimary,
    boxShadow: shadow,
    transition: `box-shadow 0.15s ${easing}, border-color 0.15s ${easing}`,
    "& fieldset": { borderColor: border },
    "&:hover fieldset": { borderColor: borderStrong },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${accentSoft}` },
    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { color: textPrimary },
  "& .MuiInputBase-input::placeholder": { color: textMuted, opacity: 1 },
  "& .MuiInputLabel-root": { color: textMuted, fontSize: "14px" },
  "& .MuiInputLabel-root.Mui-focused": { color: accent },
};

export const thSx = {
  color: textSecondary,
  fontWeight: 700,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  fontFamily: font,
  borderBottom: `1px solid ${border}`,
  backgroundColor: "transparent",
  "& .MuiTableSortLabel-root": { color: textSecondary, "&:hover": { color: accent } },
  "& .MuiTableSortLabel-root.Mui-active": { color: accent },
  "& .MuiTableSortLabel-icon": { color: `${accent} !important` },
};

// Card-style table row (no cell dividers) with the Learning Hub's hover-lift
// instead of a flat color swap.
export const rowSx = {
  cursor: "pointer",
  transition: `background-color 0.2s ${easing}, transform 0.2s ${easing}, box-shadow 0.2s ${easing}`,
  "& td": { border: "none", py: 1.5 },
  "&:hover": { backgroundColor: surfaceHover, transform: "translateY(-1px)", boxShadow: shadow },
};

export const primaryButtonSx = {
  background: `linear-gradient(135deg, ${accent} 0%, ${accentHover} 100%)`,
  color: "#fff",
  textTransform: "none",
  fontFamily: font,
  fontWeight: 700,
  fontSize: "13.5px",
  letterSpacing: "0.1px",
  borderRadius: "999px",
  padding: "12px 26px",
  minHeight: "44px",
  lineHeight: 1,
  boxShadow: "0 4px 16px rgba(192,30,46,0.32), inset 0 1px 0 rgba(255,255,255,0.16)",
  transition: `transform 0.18s ${easing}, box-shadow 0.18s ${easing}, filter 0.18s ${easing}`,
  "& .MuiButton-startIcon": { marginRight: "7px" },
  "& .MuiButton-startIcon > svg": { fontSize: "18px" },
  "&:hover": {
    boxShadow: "0 10px 26px rgba(192,30,46,0.4), inset 0 1px 0 rgba(255,255,255,0.22)",
    transform: "translateY(-2px) scale(1.015)",
    filter: "brightness(1.05)",
  },
  "&:active": { transform: "translateY(0) scale(0.98)" },
};

// Secondary/outline variant to sit next to primaryButtonSx (e.g. "Cancel").
export const secondaryButtonSx = {
  backgroundColor: "transparent",
  color: textSecondary,
  border: `1px solid ${border}`,
  textTransform: "none",
  fontFamily: font,
  fontWeight: 600,
  fontSize: "13.5px",
  borderRadius: "999px",
  padding: "12px 26px",
  minHeight: "44px",
  lineHeight: 1,
  transition: `all 0.18s ${easing}`,
  "&:hover": { backgroundColor: surfaceRaised, borderColor: borderStrong, color: textPrimary },
};

export const dialogPaperSx = {
  backgroundColor: surface,
  borderRadius: "16px",
  border: `1px solid ${border}`,
  boxShadow: shadowLifted,
  backgroundImage: "none",
};

// Stat/summary tile — matches the Learning Hub's "Continue" card: light
// surface, colored top accent, icon badge, hover lift.
/**
 * A filled button in a given colour.
 *
 * ONE IMPLEMENTATION, because the colour on a button is an encoding and an
 * encoding with three copies is three encodings. Every surface that paints an
 * action — the strip bay, the session panel, the amend row — reads from here,
 * so amber always means the same thing and always looks the same doing it.
 *
 * The colours themselves are not chosen per button. Each action wears the
 * colour that THING already has elsewhere in the product: amber is an owed
 * debrief, purple is a group class, blue is a transfer. Green is the one
 * exception and is reserved for "the room is open, go now" — nothing else is
 * green, so it never has to compete with the brand red that every ordinary
 * primary button already wears.
 */
export const filledButtonSx = (color) => ({
  minHeight: 0,
  py: 0.55,
  px: 1.4,
  fontFamily: font,
  fontSize: "12.5px",
  fontWeight: 700,
  textTransform: "none",
  borderRadius: "8px",
  color: "#fff",
  backgroundColor: color,
  boxShadow: "none",
  "&:hover": { backgroundColor: color, filter: "brightness(0.93)", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: color, opacity: 0.45, color: "#fff" },
});

/**
 * The colour each kind of session action wears. Named rather than inlined so
 * a reader can see the whole vocabulary in one place and notice when two
 * actions have accidentally been given the same meaning.
 */
export const ACTION_COLORS = {
  join: success,          // the only action with a clock running
  debrief: warning,       // matches "debrief owed" everywhere it appears
  klass: "#9333ea",       // matches the group-class block on the diary
  handover: infoBlue,     // a transfer, not a destruction
  destructive: error,     // cancel, no-show
};

export const statTileSx = {
  backgroundColor: surface,
  border: `1px solid ${border}`,
  borderRadius: "14px",
  padding: "16px",
  boxShadow: shadow,
  transition: `transform 0.2s ${easing}, box-shadow 0.2s ${easing}`,
  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 24px rgba(192,30,46,0.10)" },
};

// Rotating palette (same family as the Learning Hub's subject-card gradients)
// used to give each person a distinct, stable avatar color based on their name.
const AVATAR_PALETTE = [accent, navy, "#7c3aed", "#2563eb", "#16a34a", "#d97706"];
export function avatarColorFor(name) {
  const str = name || "?";
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

// Per-subject dark gradients — copied byte-for-byte from the Learning Hub
// (web-frontend: components/ai-learn/HomeView.jsx SUBJECT_GRADIENTS) so a
// subject reads as the same subject everywhere a student or instructor sees
// it, not a re-derived admin-portal palette.
/**
 * SUBJECT IDENTITY — TRANSCRIBED FROM web-frontend, NOT AUTHORED HERE.
 *
 * A subject has to look the same to the school as it does to the student. The
 * student's Learning Hub (/learning-hub/learn/us/ppl) is where the colours are
 * decided; this file's only job is to say the same thing.
 *
 * The US course has NO palette of its own. Every US subject borrows a UK
 * subject's gradient and artwork, and that borrowing is `ART_FOR` in
 * web-frontend/components/us-learn/UsHomeView.jsx. So the three tables below
 * are copied verbatim from three places and composed in code:
 *
 *   SUBJECT_GRADIENTS  <- components/ai-learn/HomeView.jsx      (9 UK subjects)
 *   SUBJECT_ART        <- components/us-learn/UsHomeView.jsx    (ART_FOR)
 *   SUBJECT_FLAT_UK/US <- the `color` on each subject in constants/learningPaths.js
 *                         and constants/usLearningPaths.js
 *
 * The version this replaces hand-wrote a parallel set of fifteen US gradients
 * "keyed to the same visual families as their UK counterparts". Six of the
 * fifteen came out different from the site — airspace was #1d4ed8 here against
 * #1e3a8a there, airworthiness stone against slate, weather-services teal
 * against blue — because a second table written from intent has nothing
 * keeping it honest. Copying two facts and composing them cannot drift that
 * way: get ART_FOR right and every US subject is right for free.
 *
 * Anything added to these tables belongs in web-frontend first.
 */
export const SUBJECT_GRADIENTS = {
  "meteorology":                 ["#062a3e", "#0e5a85"],
  "navigation":                  ["#3d1a00", "#92480e"],
  "principles-of-flight":        ["#1e0a3e", "#4c1d95"],
  "aircraft-general-knowledge":  ["#1a2332", "#37495e"],
  "human-performance":           ["#022c22", "#065f46"],
  "air-law":                     ["#1a1a3e", "#1e3a8a"],
  "flight-performance-planning": ["#3d2800", "#854d0e"],
  "communications":              ["#0a2e2a", "#115e59"],
  "operational-procedures":      ["#2e1065", "#5b21b6"],
};

/**
 * US subject -> the UK subject whose gradient and artwork it wears.
 * Verbatim ART_FOR. A US subject missing from here is a US subject the student
 * sees in brand red on the Learning Hub, so the fix is in web-frontend.
 */
export const SUBJECT_ART = {
  "regulations":                           "air-law",
  "airworthiness":                         "aircraft-general-knowledge",
  "principles-of-flight":                  "principles-of-flight",
  "aircraft-systems":                      "aircraft-general-knowledge",
  "flight-instruments":                    "aircraft-general-knowledge",
  "weight-and-balance":                    "flight-performance-planning",
  "aircraft-performance":                  "flight-performance-planning",
  "weather-theory":                        "meteorology",
  "weather-services":                      "meteorology",
  "airspace-and-nas":                      "air-law",
  "airport-operations-and-communications": "communications",
  "navigation-and-cross-country":          "navigation",
  "human-factors-and-aeromedical":         "human-performance",
  "aeronautical-decision-making":          "operational-procedures",
  "emergency-and-night-operations":        "operational-procedures",
};

/**
 * The flat per-subject colour, for dots, chips and bars where a full gradient
 * wash would be too heavy. This is a SEPARATE fact from the gradient, not the
 * gradient's light stop: on the site, air-law's card is #1e3a8a and air-law's
 * accent is #2563eb. Reading one off the other put the wrong blue on every
 * subject chip in this portal.
 *
 * SPLIT BY REGION, because a subject key is only unique within its own tree.
 * `principles-of-flight` exists in both and is a different colour in each
 * (#7c3aed UK, #6366f1 US) — one flat map would have had to silently pick a
 * winner, which is how the last set of subject colours went wrong. It is the
 * only key the two trees share today, and a shared key added later would be
 * caught by scripts/check_subject_colours.js rather than quietly overwriting.
 *
 * Unlike the gradients, the US course DOES author its own flat colours — the
 * fifteen are grouped into the syllabus's four phases so related subjects share
 * a hue family. So these are keyed directly, with no SUBJECT_ART hop.
 */
export const SUBJECT_FLAT_UK = {
  "principles-of-flight":        "#7c3aed",
  "aircraft-general-knowledge":  "#475569",
  "human-performance":           "#059669",
  "air-law":                     "#2563eb",
  "flight-performance-planning": "#0d9488",
  "operational-procedures":      "#d97706",
  "meteorology":                 "#0ea5e9",
  "navigation":                  "#f97316",
  "communications":              "#7e22ce",
};

export const SUBJECT_FLAT_US = {
  "regulations":                           "#4f46e5",   // 1 · foundations (indigo)
  "airworthiness":                         "#4338ca",
  "principles-of-flight":                  "#6366f1",
  "aircraft-systems":                      "#4c1d95",
  "flight-instruments":                    "#5b21b6",
  "weight-and-balance":                    "#0f766e",   // 2 · performance and weather
  "aircraft-performance":                  "#0d9488",
  "weather-theory":                        "#b45309",
  "weather-services":                      "#d97706",
  "airspace-and-nas":                      "#1d4ed8",   // 3 · airspace and navigation
  "airport-operations-and-communications": "#2563eb",
  "navigation-and-cross-country":          "#0369a1",
  "human-factors-and-aeromedical":         "#be123c",   // 4 · human factors
  "aeronautical-decision-making":          "#9f1239",
  "emergency-and-night-operations":        "#7f1d1d",
};

// DEFAULT_GRADIENT on the site. Kept identical rather than made prettier: if a
// subject renders in this, the student is seeing the same thing, and the fix is
// a missing entry upstream — not a nicer fallback here that hides the gap.
export const SUBJECT_GRADIENT_DEFAULT = ["#2a0008", "#7a1520"];
export const SUBJECT_FLAT_DEFAULT = "#475569";

// Say it once, in dev, rather than colouring over it. Every unmapped subject
// this portal has ever shown turned out to be bad DATA — a group session tagged
// "airspace" instead of "airspace-and-nas" — not a missing colour.
const warned = new Set();
function unmapped(subjectKey, what) {
  if (process.env.NODE_ENV === "production" || warned.has(subjectKey + what)) return;
  warned.add(subjectKey + what);
  // eslint-disable-next-line no-console
  console.warn(
    `[schoolPortal] no ${what} for subject "${subjectKey}". Either it is not a ` +
    `real subject key (check constants/usPplTopicTree.js on the backend), or ` +
    `web-frontend added a subject this file has not been told about.`
  );
}

/** The UK art/gradient key a subject resolves to. US hops through ART_FOR. */
export function subjectArtKey(subjectKey) {
  return SUBJECT_ART[subjectKey] || subjectKey;
}

export function subjectGradient(subjectKey) {
  if (!subjectKey) return SUBJECT_GRADIENT_DEFAULT;
  const g = SUBJECT_GRADIENTS[subjectArtKey(subjectKey)];
  if (!g) { unmapped(subjectKey, "gradient"); return SUBJECT_GRADIENT_DEFAULT; }
  return g;
}

export function subjectGradientCss(subjectKey) {
  const [g0, g1] = subjectGradient(subjectKey);
  return `linear-gradient(135deg, ${g0}, ${g1})`;
}

/**
 * `region` is only needed for a key both trees define. Everything else resolves
 * from whichever tree owns it, so callers that genuinely do not know the region
 * — and most do not — still get the right colour.
 */
export function subjectAccentColor(subjectKey, region) {
  if (!subjectKey) return SUBJECT_FLAT_DEFAULT;
  const us = SUBJECT_FLAT_US[subjectKey];
  const uk = SUBJECT_FLAT_UK[subjectKey];
  if (us && uk) return String(region).toUpperCase() === "US" ? us : uk;
  const c = us || uk;
  if (!c) { unmapped(subjectKey, "accent colour"); return SUBJECT_FLAT_DEFAULT; }
  return c;
}

// Subject labels, also transcribed rather than derived. formatSubjectName's
// title-case fallback turned "airport-operations-and-communications" into
// "Airport Operations And Communications" — capital And, and not what the
// student is reading either way. The US labels are the `label` on each subject
// in web-frontend/constants/derived/us_ppl_learning_paths.json.
export const SUBJECT_DISPLAY_NAMES = {
  // UK
  "air-law": "Air Law",
  "human-performance": "Human Performance",
  "meteorology": "Meteorology",
  "navigation": "Navigation",
  "communications": "Communications",
  "principles-of-flight": "Principles of Flight",
  "operational-procedures": "Operational Procedures",
  "flight-performance-planning": "Flight Performance & Planning",
  "aircraft-general-knowledge": "Aircraft General Knowledge",
  // US Part 61
  "regulations": "Federal Aviation Regulations",
  "airworthiness": "Airworthiness Requirements",
  "aircraft-systems": "Aircraft Systems and Powerplants",
  "flight-instruments": "Flight Instruments",
  "weight-and-balance": "Weight and Balance",
  "aircraft-performance": "Aircraft Performance and Limitations",
  "weather-theory": "Weather Theory",
  "weather-services": "Weather Products and Briefings",
  "airspace-and-nas": "Airspace and the National Airspace System",
  "airport-operations-and-communications": "Airport Operations and Radio Communications",
  "navigation-and-cross-country": "Navigation and Cross-Country Planning",
  "human-factors-and-aeromedical": "Human Factors and Aeromedical",
  "aeronautical-decision-making": "Aeronautical Decision Making and Risk Management",
  "emergency-and-night-operations": "Emergency and Night Operations",
};

export function formatSubjectName(subjectKey) {
  return SUBJECT_DISPLAY_NAMES[subjectKey] || (subjectKey || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

// PPL subject order — matches config/subjects.js on the backend.
export const PPL_SUBJECTS = [
  "air-law", "human-performance", "meteorology", "navigation", "communications",
  "principles-of-flight", "operational-procedures", "flight-performance-planning",
  "aircraft-general-knowledge",
];

// US Part 61 subject order — matches constants/usPplTopicTree.js listSubjects()
// on the backend. The labels are already in SUBJECT_DISPLAY_NAMES above.
export const US_PPL_SUBJECTS = [
  "regulations", "airworthiness", "principles-of-flight", "aircraft-systems",
  "flight-instruments", "weight-and-balance", "aircraft-performance",
  "weather-theory", "weather-services", "airspace-and-nas",
  "airport-operations-and-communications", "navigation-and-cross-country",
  "human-factors-and-aeromedical", "aeronautical-decision-making",
  "emergency-and-night-operations",
];

/**
 * The subjects a region's course actually has.
 *
 * Three pickers used PPL_SUBJECTS unconditionally — what an instructor covers,
 * the propose-a-session subject, and signup — so a US instructor could only
 * choose UK papers. Their per-subject prices were stored under UK keys and
 * never matched a US booking, and a US student's weakest subject was not in
 * the dropdown it was meant to pre-fill.
 */
export const subjectsForRegion = (region) =>
  String(region).toUpperCase() === "US" ? US_PPL_SUBJECTS : PPL_SUBJECTS;
